import { Injectable } from '@angular/core';
import {
  type AddressSuggestion,
  type BanFeatureProperties,
  addressFromBan,
} from '../domain/address';
import type { Coordinates } from '../domain/geo';

const BAN_BASE = 'https://api-adresse.data.gouv.fr';

interface BanFeature {
  readonly properties?: BanFeatureProperties;
  readonly geometry?: {
    readonly type?: string;
    readonly coordinates?: readonly number[];
  };
}

interface BanResponse {
  readonly features?: readonly BanFeature[];
}

/**
 * Accès à la Base Adresse Nationale (adresse.data.gouv.fr).
 *
 * Gratuit, sans clé, données françaises. On l'appelle depuis le navigateur :
 * l'API accepte le CORS, et aucune donnée personnelle n'est stockée chez nous
 * au passage — seulement l'adresse que l'utilisateur choisit d'enregistrer.
 */
@Injectable({ providedIn: 'root' })
export class BanAddressService {
  async search(query: string, limit = 5): Promise<readonly AddressSuggestion[]> {
    const trimmed = query.trim();
    if (trimmed.length < 3) {
      return [];
    }

    const url = new URL(`${BAN_BASE}/search/`);
    url.searchParams.set('q', trimmed);
    url.searchParams.set('limit', String(limit));
    url.searchParams.set('autocomplete', '1');

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`BAN search ${response.status}`);
    }
    return parseAddressFeatures(await response.json());
  }

  /**
   * Résout une requête textuelle en coordonnées (adresse complète ou commune).
   *
   * Utilisé pour situer le secteur d'un client qui a déclaré une commune sans
   * activer le GPS. La position n'est pas persistée : on la recalcule à la
   * demande à partir du texte du profil.
   */
  async geocode(
    query: string,
    options?: { readonly municipalityOnly?: boolean },
  ): Promise<Coordinates | undefined> {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      return undefined;
    }

    const url = new URL(`${BAN_BASE}/search/`);
    url.searchParams.set('q', trimmed);
    url.searchParams.set('limit', '1');
    url.searchParams.set('autocomplete', '0');
    if (options?.municipalityOnly) {
      url.searchParams.set('type', 'municipality');
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`BAN geocode ${response.status}`);
    }
    const body = (await response.json()) as BanResponse;
    return coordinatesFromBan(body.features?.[0]);
  }

  async reverse(position: Coordinates): Promise<AddressSuggestion | undefined> {
    const url = new URL(`${BAN_BASE}/reverse/`);
    url.searchParams.set('lon', String(position.longitude));
    url.searchParams.set('lat', String(position.latitude));

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`BAN reverse ${response.status}`);
    }
    return parseAddressFeatures(await response.json())[0];
  }
}

function parseAddressFeatures(body: BanResponse): AddressSuggestion[] {
  const features = body.features ?? [];
  const suggestions: AddressSuggestion[] = [];
  for (const feature of features) {
    const address = feature.properties ? addressFromBan(feature.properties) : undefined;
    if (address !== undefined) {
      suggestions.push(address);
    }
  }
  return suggestions;
}

/** BAN renvoie GeoJSON `[longitude, latitude]`. */
function coordinatesFromBan(feature: BanFeature | undefined): Coordinates | undefined {
  const coordinates = feature?.geometry?.coordinates;
  if (coordinates === undefined || coordinates.length < 2) {
    return undefined;
  }
  const longitude = coordinates[0];
  const latitude = coordinates[1];
  if (
    typeof longitude !== 'number' ||
    typeof latitude !== 'number' ||
    !Number.isFinite(longitude) ||
    !Number.isFinite(latitude)
  ) {
    return undefined;
  }
  return { latitude, longitude };
}
